'use client';

/**
 * The end-of-lesson screen.
 *
 * Three jobs, in this order of importance:
 *   1. Celebrate what the child just did.
 *   2. Tell them the ONE thing to change next time (never a list of failings).
 *   3. Make the next lesson the easiest thing on the screen to click.
 *
 * The trouble-keys panel is the only critical feedback shown, and it is framed
 * as "keys to say hello to" rather than as errors.
 */
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { RotateCcw, ArrowRight, Map } from 'lucide-react';
import type { LessonDef, LessonScore, LevelDef } from '../types';
import type { FinishOutcome } from '../context/KidsProvider';
import { Confetti, StarRow } from './Celebration';
import { getAchievement } from '../data/achievements';
import { nextLesson } from '../data/curriculum';
import { charLabel, fingerForChar, FINGERS } from '../lib/keyboard';
import { ACCURACY_TIPS, CHEERS, TECHNIQUE_TIPS } from '../data/content';
import { Pill } from './ui';

export interface SummaryData {
  score: LessonScore;
  outcome: FinishOutcome;
  lesson: LessonDef;
  level: LevelDef;
}

export default function LessonSummary({
  data,
  onRetry,
  confettiKey,
}: {
  data: SummaryData;
  onRetry: () => void;
  confettiKey: number;
}) {
  const { score, outcome, lesson, level } = data;
  const next = nextLesson(lesson.id);
  const [revealed, setRevealed] = useState(false);

  // Let the stars land before the numbers animate in — the sequence is what
  // makes it feel like a reward rather than a report.
  useEffect(() => {
    const id = window.setTimeout(() => setRevealed(true), 620);
    return () => window.clearTimeout(id);
  }, []);

  // Deterministic pick so the message does not change on re-render.
  const cheer = CHEERS[(score.chars + score.stars) % CHEERS.length];
  // Corrective advice is only ever shown when it is warranted. Drawing the
  // fallback from a pool that contained "slow down" meant a three-star run
  // could be told off for going too fast.
  const tip =
    score.accuracy < 92
      ? ACCURACY_TIPS[score.chars % ACCURACY_TIPS.length]
      : score.stars < 3 && score.netWpm < lesson.targetWpm
        ? 'Your accuracy is there. Now let your fingers relax and flow a little.'
        : TECHNIQUE_TIPS[score.chars % TECHNIQUE_TIPS.length];

  return (
    <div className="relative max-w-2xl mx-auto px-4 sm:px-6 py-6">
      <Confetti trigger={confettiKey} />

      <div className="k-card-lg p-6 sm:p-8 text-center k-anim-rise">
        <p className="k-eyebrow">
          {level.emoji} Level {level.index} · {lesson.title}
        </p>

        <StarRow stars={score.stars} size={52} animate />

        <h1 className="mt-3" style={{ fontSize: 'clamp(1.5rem,1.2rem+1.6vw,2rem)' }}>
          {score.stars === 3 ? 'Perfect run!' : cheer}
        </h1>

        {outcome.firstClear ? (
          <p className="text-muted-foreground mt-1">
            Lesson complete — {next ? 'the next one is unlocked.' : 'that was the last one!'}
          </p>
        ) : outcome.improved ? (
          <p className="text-muted-foreground mt-1">You beat your previous best on this lesson.</p>
        ) : (
          <p className="text-muted-foreground mt-1">Good practice. Replays earn a little less.</p>
        )}

        {/* ── the numbers ───────────────────────────────────────────────
            No words-per-minute. A child cannot act on it, and putting it here
            teaches them to chase the wrong thing — the whole star system is
            built to reward accuracy instead. Speed is still recorded and is
            shown to grown-ups on /kids/progress and /kids/parents.

            What is left is the two things a child can actually do something
            about: how many they got right first time, and how many slipped. */}
        <dl className={`grid grid-cols-3 gap-2 mt-6 ${revealed ? 'k-stagger' : 'opacity-0'}`}>
          <Metric
            i={0}
            label="Got it first try"
            value={`${Math.round(score.accuracy)}`}
            unit="%"
            color="--k-mint"
          />
          <Metric i={1} label="Oops" value={`${score.mistakes}`} unit="" color="--k-melon" />
          <Metric i={2} label="Time" value={`${score.seconds}`} unit="s" color="--k-mango" />
        </dl>

        {/* ── rewards ─────────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-center justify-center gap-2 mt-5">
          <Pill color="--k-grape">+{outcome.xpEarned} XP</Pill>
          <Pill color="--k-mango">🪙 +{outcome.coinsEarned}</Pill>
          {score.netWpm >= lesson.targetWpm && <Pill color="--k-sky">💨 Nice and smooth</Pill>}
          {score.accuracy === 100 && <Pill color="--k-mint">💎 Flawless</Pill>}
        </div>

        {outcome.rankUp && (
          <div
            className="mt-5 p-4 rounded-2xl k-anim-pop"
            style={{ background: 'var(--k-grape-soft)', border: '2px solid var(--k-grape)' }}
          >
            <p className="font-extrabold" style={{ color: 'var(--k-grape)' }}>
              {outcome.rankUp.emoji} New rank: {outcome.rankUp.to}!
            </p>
            <p className="text-sm text-muted-foreground mt-0.5">
              You were {outcome.rankUp.from}. Keep going.
            </p>
          </div>
        )}

        {outcome.newAchievements.length > 0 && (
          <ul className="mt-4 grid gap-2 text-left">
            {outcome.newAchievements.map((id) => {
              const badge = getAchievement(id);
              if (!badge) return null;
              return (
                <li
                  key={id}
                  className="k-anim-pop flex items-center gap-3 p-3 rounded-2xl"
                  style={{ background: 'var(--k-mango-soft)', border: '2px solid var(--k-mango)' }}
                >
                  <span style={{ fontSize: 28 }} aria-hidden="true">
                    {badge.emoji}
                  </span>
                  <span className="min-w-0">
                    <span className="block font-extrabold">Badge earned: {badge.name}</span>
                    <span className="block text-sm text-muted-foreground">{badge.description}</span>
                  </span>
                  <span className="ml-auto text-sm font-extrabold whitespace-nowrap">
                    🪙 +{badge.coins}
                  </span>
                </li>
              );
            })}
          </ul>
        )}

        {/* ── one piece of coaching ───────────────────────────────────── */}
        {score.trouble.length > 0 && (
          <div className="mt-6 p-4 rounded-2xl text-left" style={{ background: 'var(--muted)' }}>
            <p className="k-eyebrow">Keys to say hello to</p>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              {score.trouble.map((char) => (
                <span
                  key={char}
                  className="k-pill"
                  style={{
                    background: `color-mix(in srgb, var(${FINGERS[fingerForChar(char)].cssVar}) 18%, transparent)`,
                    fontFamily: 'var(--font-mono)',
                    fontSize: '0.95rem',
                  }}
                >
                  {charLabel(char)}
                  <span className="font-bold opacity-70 text-[0.7rem]">
                    {FINGERS[fingerForChar(char)].label}
                  </span>
                </span>
              ))}
            </div>
            <p className="text-sm text-muted-foreground mt-2">{tip}</p>
          </div>
        )}

        {/* ── what next ───────────────────────────────────────────────── */}
        <div className="flex flex-wrap gap-2 justify-center mt-7">
          {next ? (
            <Link href={`/kids/lesson/${next.id}`} className="k-btn k-btn-success">
              Next lesson
              <ArrowRight size={18} aria-hidden="true" />
            </Link>
          ) : (
            <Link href="/kids/certificate" className="k-btn k-btn-success">
              🎓 Get your certificate
            </Link>
          )}
          <button type="button" onClick={onRetry} className="k-btn k-btn-secondary">
            <RotateCcw size={17} aria-hidden="true" />
            {score.stars < 3 ? 'Try for 3 stars' : 'Play again'}
          </button>
          <Link href={`/kids/journey/${level.slug}`} className="k-btn k-btn-ghost">
            <Map size={17} aria-hidden="true" />
            Level map
          </Link>
        </div>
      </div>
    </div>
  );
}

function Metric({
  label,
  value,
  unit,
  color,
  i,
}: {
  label: string;
  value: string;
  unit: string;
  color: string;
  i: number;
}) {
  return (
    <div className="k-card p-3" style={{ ['--k-i' as string]: i }}>
      <dt className="k-eyebrow">{label}</dt>
      <dd
        className="font-extrabold leading-none mt-1 tabular-nums"
        style={{ fontSize: '1.6rem', color: `var(${color})` }}
      >
        {value}
        <span className="text-xs ml-0.5 opacity-70">{unit}</span>
      </dd>
    </div>
  );
}
